import { Popover } from '@headlessui/react';
import { useState } from 'react';
import { CgChevronDown } from 'react-icons/cg';

import { IProductIdWithHaggle } from '@/interfaces/api/product/IProductIdWithHaggle';
import { IProductWithHaggle } from '@/interfaces/api/product/IProductWithHaggle';

export interface FilterState {
	vendor: string[];
	category: string[];
	priceRange: {
		min: number;
		max: number;
	};
	haggleStatus: string[];
	country: string[];
}

interface ShopifyFiltersProps {
	products: IProductWithHaggle[] | IProductIdWithHaggle[];
	onFilterChange: (filters: FilterState) => void;
	initialFilters?: Partial<FilterState>;
	className?: string;
}

export const ShopifyFilters = ({
	products,
	onFilterChange,
	initialFilters,
	className = '',
}: ShopifyFiltersProps) => {
	const [filters, setFilters] = useState<FilterState>({
		vendor: [],
		category: [],
		priceRange: { min: 0, max: 10000 },
		haggleStatus: [],
		country: [],
		...initialFilters,
	});
	const [error, setError] = useState<string | null>(null);

	// Extract unique values from products
	const vendors = [...new Set(products.map((p) => p.vendor))];
	const categories = [...new Set(products.map((p) => p.product_type))];
	const countries = [
		...new Set(
			products
				.map((p) => p.store?.country_name)
				.filter((country): country is string => !!country),
		),
	];

	const haggleStatusOptions = [
		{ id: 'all', label: 'All' },
		{ id: 'live', label: 'Live' },
		{ id: 'upcoming', label: 'Coming Soon' },
	];

	const handleFilterChange = (
		type: keyof FilterState,
		value: FilterState[typeof type],
	) => {
		const newFilters = { ...filters, [type]: value };
		setFilters(newFilters);
		onFilterChange(newFilters);
	};

	const renderSelectedFilters = (type: keyof FilterState) => {
		switch (type) {
			case 'haggleStatus':
				return filters.haggleStatus.length > 0
					? filters.haggleStatus
							.map(
								(status) =>
									haggleStatusOptions.find((opt) => opt.id === status)?.label,
							)
							.join(', ')
					: 'All';
			case 'vendor':
				return filters.vendor.length > 0
					? filters.vendor.join(', ')
					: 'All Brands';
			case 'category':
				return filters.category.length > 0
					? filters.category.join(', ')
					: 'All Categories';
			case 'priceRange':
				if (filters.priceRange.min === 0 && filters.priceRange.max === 10000) {
					return '';
				}
				return `$${filters.priceRange.min} - $${filters.priceRange.max}`;
			case 'country':
				return filters.country.length > 0
					? filters.country.join(', ')
					: 'All Countries';
			default:
				return 'All';
		}
	};

	return (
		<div className="w-full">
			<div className={`bg-white rounded-xl shadow-lg p-4 ${className}`}>
				<div
					className={`flex gap-2 items-center justify-between flex-wrap ${className}`}
				>
					<div className="flex gap-2 items-center flex-wrap z-30">
						<Popover className="relative">
							{({ open }) => (
								<>
									<Popover.Button
										className={`px-4 py-2 border rounded-lg flex flex-col items-start gap-1 text-sm ${
											open ? 'bg-purpleBrand-50 border-purpleBrand-900' : ''
										}`}
									>
										<div className="flex items-center gap-2">
											<span>Category</span>
											<CgChevronDown
												className={`w-4 h-4 transition-transform ${
													open ? 'rotate-180' : ''
												}`}
											/>
										</div>
										{filters.category.length > 0 && (
											<span className="text-xs bg-gray-200 px-2 py-1 rounded-full truncate max-w-[150px]">
												{renderSelectedFilters('category')}
											</span>
										)}
									</Popover.Button>
									<Popover.Panel className="absolute z-10 mt-2 bg-white rounded-lg shadow-lg p-4 w-[250px] border border-gray-200">
										<div className="mb-3 text-sm text-gray-600">
											Filter Category
										</div>
										<div className="space-y-2 max-h-60 overflow-y-auto">
											{categories.map((category) => (
												<label
													key={category}
													className="flex items-center space-x-2"
												>
													<input
														type="checkbox"
														checked={filters.category.includes(category)}
														onChange={(e) => {
															const newCategories = e.target.checked
																? [...filters.category, category]
																: filters.category.filter(
																		(c) => c !== category,
																  );
															handleFilterChange('category', newCategories);
														}}
														className="rounded text-blue-600"
													/>
													<span className="text-sm">
														{category || 'Uncategorized'}
													</span>
												</label>
											))}
										</div>
									</Popover.Panel>
								</>
							)}
						</Popover>

						<Popover className="relative">
							{({ open }) => (
								<>
									<Popover.Button
										className={`px-4 py-2 border rounded-lg flex flex-col items-start gap-1 text-sm ${
											open ? 'bg-purpleBrand-50 border-purpleBrand-900' : ''
										}`}
									>
										<div className="flex items-center gap-2">
											<span>Merchants</span>
											<CgChevronDown
												className={`w-4 h-4 transition-transform ${
													open ? 'rotate-180' : ''
												}`}
											/>
										</div>
										{filters.vendor.length > 0 && (
											<span className="text-xs bg-gray-200 px-2 py-1 rounded-full truncate max-w-[150px]">
												{renderSelectedFilters('vendor')}
											</span>
										)}
									</Popover.Button>
									<Popover.Panel className="absolute z-10 mt-2 bg-white rounded-lg shadow-lg p-4 w-[250px] border border-gray-200">
										<div className="mb-3 text-sm text-gray-600">
											Filter Merchants
										</div>
										<div className="space-y-2 max-h-60 overflow-y-auto">
											{vendors.map((vendor) => (
												<label
													key={vendor}
													className="flex items-center space-x-2"
												>
													<input
														type="checkbox"
														checked={filters.vendor.includes(vendor)}
														onChange={(e) => {
															const newVendors = e.target.checked
																? [...filters.vendor, vendor]
																: filters.vendor.filter((v) => v !== vendor);
															handleFilterChange('vendor', newVendors);
														}}
														className="rounded text-blue-600"
													/>
													<span className="text-sm">{vendor}</span>
												</label>
											))}
										</div>
									</Popover.Panel>
								</>
							)}
						</Popover>

						<Popover className="relative">
							{({ open }) => (
								<>
									<Popover.Button
										className={`px-4 py-2 border rounded-lg flex flex-col items-start gap-1 text-sm ${
											open ? 'bg-purpleBrand-50 border-purpleBrand-900' : ''
										}`}
									>
										<div className="flex items-center gap-2">
											<span>Price Range</span>
											<CgChevronDown
												className={`w-4 h-4 transition-transform ${
													open ? 'rotate-180' : ''
												}`}
											/>
										</div>
										{renderSelectedFilters('priceRange') && (
											<span className="text-xs bg-gray-200 px-2 py-1 rounded-full truncate max-w-[150px]">
												{renderSelectedFilters('priceRange')}
											</span>
										)}
									</Popover.Button>
									<Popover.Panel className="absolute z-10 mt-2 bg-white rounded-lg shadow-lg p-4 w-[300px]">
										<div className="mb-3 text-sm text-gray-600">
											Filter Price Range
										</div>
										<div className="flex items-center space-x-4">
											<input
												type="number"
												placeholder="Min"
												className="w-full p-2 border rounded"
												value={filters.priceRange.min}
												min="0"
												onChange={(e) => {
													const newMin = Number(e.target.value);
													if (newMin > filters.priceRange.max) {
														setError(
															'Min price cannot be greater than max price',
														);
													} else {
														setError(null);
														handleFilterChange('priceRange', {
															...filters.priceRange,
															min: newMin,
														});
													}
												}}
											/>
											<span>to</span>
											<input
												type="number"
												placeholder="Max"
												className="w-full p-2 border rounded"
												value={filters.priceRange.max}
												min="0"
												onChange={(e) => {
													const newMax = Number(e.target.value);
													if (newMax < filters.priceRange.min) {
														setError('Max price cannot be less than min price');
													} else {
														setError(null);
														handleFilterChange('priceRange', {
															...filters.priceRange,
															max: newMax,
														});
													}
												}}
											/>
										</div>
										{error && (
											<div className="text-red-500 text-sm mt-2">{error}</div>
										)}
									</Popover.Panel>
								</>
							)}
						</Popover>

						<Popover className="relative">
							{({ open }) => (
								<>
									<Popover.Button
										className={`px-4 py-2 border rounded-lg flex flex-col items-start gap-1 text-sm ${
											open ? 'bg-purpleBrand-50 border-purpleBrand-900' : ''
										}`}
									>
										<div className="flex items-center gap-2">
											<span>Region</span>
											<CgChevronDown
												className={`w-4 h-4 transition-transform ${
													open ? 'rotate-180' : ''
												}`}
											/>
										</div>
										{filters.country.length > 0 && (
											<span className="text-xs bg-gray-200 px-2 py-1 rounded-full truncate max-w-[150px]">
												{renderSelectedFilters('country')}
											</span>
										)}
									</Popover.Button>
									<Popover.Panel className="absolute z-10 mt-2 bg-white rounded-lg shadow-lg p-4 w-[250px] border border-gray-200">
										<div className="mb-3 text-sm text-gray-600">
											Filter Region
										</div>
										<div className="space-y-2 max-h-60 overflow-y-auto">
											{countries.map((country) => (
												<label
													key={country}
													className="flex items-center space-x-2"
												>
													<input
														type="checkbox"
														checked={filters.country.includes(country)}
														onChange={(e) => {
															const newCountries = e.target.checked
																? [...filters.country, country]
																: filters.country.filter((c) => c !== country);
															handleFilterChange('country', newCountries);
														}}
														className="rounded text-blue-600"
													/>
													<span className="text-sm">{country}</span>
												</label>
											))}
										</div>
									</Popover.Panel>
								</>
							)}
						</Popover>

						<Popover className="relative">
							{({ open }) => (
								<>
									<Popover.Button
										className={`px-4 py-2 border rounded-lg flex flex-col items-start gap-1 text-sm ${
											open ? 'bg-purpleBrand-50 border-purpleBrand-900' : ''
										}`}
									>
										<div className="flex items-center gap-2">
											<span>Calendar</span>
											<CgChevronDown
												className={`w-4 h-4 transition-transform ${
													open ? 'rotate-180' : ''
												}`}
											/>
										</div>
										{filters.haggleStatus.length > 0 && (
											<span className="text-xs bg-gray-200 px-2 py-1 rounded-full truncate max-w-[150px]">
												{renderSelectedFilters('haggleStatus')}
											</span>
										)}
									</Popover.Button>
									<Popover.Panel className="absolute z-10 mt-2 bg-white rounded-lg shadow-lg p-4 w-[250px] border border-gray-200">
										<div className="mb-3 text-sm text-gray-600">
											Filter Haggle Status
										</div>
										<div className="space-y-2">
											{haggleStatusOptions.map((status) => (
												<label
													key={status.id}
													className="flex items-center space-x-2"
												>
													<input
														type="checkbox"
														checked={filters.haggleStatus.includes(status.id)}
														onChange={(e) => {
															const newStatus = e.target.checked
																? [...filters.haggleStatus, status.id]
																: filters.haggleStatus.filter(
																		(s) => s !== status.id,
																  );
															handleFilterChange('haggleStatus', newStatus);
														}}
														className="rounded text-blue-600"
													/>
													<span className="text-sm">{status.label}</span>
												</label>
											))}
										</div>
									</Popover.Panel>
								</>
							)}
						</Popover>
					</div>

					<button
						onClick={() => {
							const defaultFilters: FilterState = {
								vendor: [],
								category: [],
								priceRange: { min: 0, max: 10000 },
								haggleStatus: [],
								country: [],
							};
							setFilters(defaultFilters);
							onFilterChange(defaultFilters);
						}}
						className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
					>
						Reset Filters
					</button>
				</div>
			</div>
		</div>
	);
};
